// versionWatch.mjs — 「这个标签页跑的还是不是最新那一版」（SB-134，第一百一十四批）。
//
// ── 它治的是哪一半 ──────────────────────────────────────────────────────────
// 这个病本仓付过一次账，`server.cpp` 的 post-routing 段逐字记着：浏览器攥着缓存包，
// 于是每次前端部署对它**静默失效数小时到数天**，症状是两份「桌面还是坏的、手机好了」
// 的报告——而修复其实早就上线了。当时把壳改成 `no-cache`，修掉了缓存那一半。
//
// **剩下的一半是 `no-cache` 管不着的**：它只在浏览器**重新加载**时起作用。
// 一个从早上开到晚上、从不 reload 的标签页永远拿不到新包，而它正跑着旧 JS
// 对着新后端说话。新功能对它不存在，改过的契约它不知道。
//
// ── 消息怎么到达：搭已经在跑的那趟车 ────────────────────────────────────────
// 后端给每条 `/api/*` 应答盖一枚 `X-Build-Rev`。前端**不新开轮询**——通知铃每 60 秒
// 本来就要拉一次，戳搭那趟车。加一条自己的轮询就是加一条会漂、会失败、会被人
// 关掉的东西，而它要回答的问题在既有流量里已经有答案了。
//
// ── 第一次见到的那枚戳就是「本页启动时的版本」 ──────────────────────────────
// 不从构建期注入：页面刚加载完就会打第一条 API，那一刻服务端的戳按定义就是这一页
// 拿到的那一版。自校准，且少一处构建期与运行期可能对不上的地方。

// 空闲多久算「这个标签页没人在用」。10 分钟是拍的数，取舍写在这里：
// 太短会在有人读长文档时把他的滚动位置刷掉（烦人但不毁东西）；
// 太长则「长期开在副屏上」那种标签页迟迟切不过去，而那恰恰是本模块的靶子。
export const IDLE_MS = 10 * 60 * 1000;

// 一枚 rev 只自动刷一次。**这一条是防死循环的**：若某天代理层把旧包缓存住了，
// 刷新之后拿到的还是旧版 ⇒ 仍然 stale ⇒ 再刷 ⇒ 无限刷新，而用户什么都做不了。
// 记在 sessionStorage 里（跟着标签页走，关了就忘）。
export const RELOADED_KEY = 'dc.versionWatch.autoReloadedFor';

// 纯函数：本页启动时的戳 + 服务端当前的戳 → 是不是旧了。
// 两侧任一为空（老后端不盖头、首帧还没拿到）一律判**不旧**——
// 一条因为"读不到"就喊新版本的横幅，会在服务端偶尔漏头时反复骚扰，
// 而它要防的那件事并不会因此被更早发现。
export function isStale(bootRev, serverRev) {
  if (!bootRev || !serverRev) return false;
  if (bootRev === 'unknown' || serverRev === 'unknown') return false;
  return bootRev !== serverRev;
}

// 纯函数：现在能不能**自动**刷。三个条件全要满足，而它们各自挡的是一种具体的破坏：
//   · stale        —— 不旧就不刷（废话，但它是这个函数唯一的正当理由）
//   · 安全时机     —— 标签页隐藏，**或**足够久没人碰过它。隐藏的页不会正被打字；
//                     长时间无操作的页也不会。这两条合起来盖住了「长期开在副屏上」
//                     那种——它永远不隐藏，但也永远没人碰。
//   · 没有焦点在输入框里 —— 最后一道。哪怕计时器认为空闲，只要光标还停在一个
//                     input/textarea/contenteditable 里，就一律不刷：
//                     **本仓的工单提交面没有草稿持久化**，刷掉就是真没了。
export function shouldAutoReload({ stale, hidden, msSinceInteraction, editing,
                                   idleMs = IDLE_MS }) {
  if (!stale) return false;
  if (editing) return false;
  return hidden === true || msSinceInteraction >= idleMs;
}

// 这一枚 rev 是不是已经自动刷过一次了（防死循环，见 RELOADED_KEY）。
export function alreadyReloadedFor(rev, storage) {
  try { return storage?.getItem(RELOADED_KEY) === rev; } catch { return false; }
}
export function markReloadedFor(rev, storage) {
  try { storage?.setItem(RELOADED_KEY, rev); } catch { /* 隐私模式下写不了，忽略 */ }
}

// 焦点是不是落在一个能打字的地方。放在这里而不是 JSX 里，理由与本模块其余部分
// 相同：它是一条**可判定**的规则，而写在 JSX 里的规则没有判据。
export function isEditing(doc) {
  const el = doc?.activeElement;
  if (!el) return false;
  if (el.isContentEditable) return true;
  const tag = String(el.tagName || '').toLowerCase();
  return tag === 'input' || tag === 'textarea' || tag === 'select';
}
